import {apiFetch} from './WorkSheetContext';
import {buildFollowUpPresets} from './crmContactApi';
import {normalizeWorkItems} from './myDayWorkItems';

/**
 * Sales follow-ups ("Teendők") — API client + pure helpers.
 * Backend: `salesFollowUp.controller.js` / `salesInbox.controller.js` / `leadContact.controller.js`
 * (routes newer than the shipped prod spec, so raw HTTP like `crmContactApi`).
 */

/** Follow-up types (mirrors backend `FOLLOW_UP_TYPES`). */
export const FOLLOW_UP_TYPE_KEYS = Object.freeze(['call', 'email', 'send-quote', 'meeting', 'sms', 'other']);

/** Tabs of the to-do screen, in bar order. */
export const FOLLOW_UP_TABS = Object.freeze(['today', 'overdue', 'upcoming', 'done']);

/** Unified-customer sources the backend accepts as `customerSource`. */
export const CUSTOMER_SOURCES = Object.freeze(['restaurant', 'partner', 'guest', 'serviceModule']);

/** Allowed values of the "upcoming days" selector. */
export const UPCOMING_DAY_OPTIONS = Object.freeze([2, 3, 7]);

/** AsyncStorage keys. */
export const GROUP_MODE_STORAGE_KEY = 'sales:followUps:groupMode';
export const AUTO_OPEN_STORAGE_PREFIX = 'sales:followUps:autoOpen:';
export const AUTO_OPEN_OPTOUT_PREFIX = 'sales:followUps:autoOpenOptOut:';

const idOf = (value) => (value && typeof value === 'object' ? (value._id || value.id) : value);
const asArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);
const toTime = (value) => {
    const t = new Date(value).getTime();

    return Number.isNaN(t) ? null : t;
};

/**
 * Client UTC offset in minutes EAST of UTC (what the backend `tzOffset` expects).
 * @param {Date} [now] reference date.
 * @returns {number} offset minutes.
 */
export const getTzOffset = (now = new Date()) => {
    const raw = -now.getTimezoneOffset();

    return raw === 0 ? 0 : raw;
};

/**
 * Local `YYYY-MM-DD` of a date (device time zone).
 * @param {Date} [now] date.
 * @returns {string} key.
 */
export const localDayKey = (now = new Date()) => {
    const p = (n) => String(n).padStart(2, '0');

    return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
};

/**
 * Greeting bucket by hour of day.
 * @param {Date} [now] reference.
 * @returns {'morning'|'afternoon'|'evening'} bucket.
 */
export const greetingKey = (now = new Date()) => {
    const h = now.getHours();

    if (h < 12) {
        return 'morning';
    }

    return h < 18 ? 'afternoon' : 'evening';
};

/**
 * Snooze presets: 15 min / 1 hour / tomorrow 9:00 (the "custom" tile is handled by the UI).
 * @param {Date} [now] reference.
 * @returns {{key: string, minutes?: number, date: Date}[]} presets (all in the future).
 */
export const buildSnoozePresets = (now = new Date()) => {
    const tomorrow = new Date(now);

    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);

    return [
        {key: 'min15', minutes: 15, date: new Date(now.getTime() + 15 * 60000)},
        {key: 'hour1', minutes: 60, date: new Date(now.getTime() + 60 * 60000)},
        {key: 'tomorrow9', date: tomorrow},
    ];
};

/** Due-date presets of the "new follow-up" sheet (shared with the contact sheet). */
export const buildDuePresets = buildFollowUpPresets;

/**
 * Normalized display entity of a follow-up (populated lead wins over denormalized snapshot fields).
 * @param {object} item follow-up.
 * @returns {{type: string, id: string, name: string, company: string, phone: string, email: string}} entity.
 */
export const entityOf = (item = {}) => {
    const lead = item.lead && typeof item.lead === 'object' ? item.lead : null;
    const type = item.entityType === 'customer' || (!lead && item.customer) ? 'customer' : 'lead';

    return {
        type,
        id: String(type === 'lead' ? (idOf(item.lead) || '') : (idOf(item.customer) || '')),
        name: plainText((lead && lead.name) || item.entityName),
        company: plainText((lead && lead.company) || item.entityCompany),
        phone: plainText((lead && lead.phone) || item.entityPhone),
        email: plainText((lead && lead.email) || item.entityEmail),
    };
};

/**
 * Tab (`overdue` | `today` | `upcoming` | `done`) that currently contains a follow-up, or null when it is in none of them.
 * @param {object} agenda normalized agenda. @param {object[]} done loaded "done" items. @param {string} id follow-up id.
 * @returns {string|null} tab key.
 */
export const resolveFocusTab = (agenda, done, id) => {
    const key = String(id || '');
    const has = (list) => asArray(list).some((i) => String(idOf(i) || '') === key);

    if (!key || !agenda) {
        return null;
    }

    if (has(agenda.overdue)) {
        return 'overdue';
    }

    if (has(agenda.today)) {
        return 'today';
    }

    if (has(agenda.upcoming && agenda.upcoming.followUps)) {
        return 'upcoming';
    }

    return has(done) ? 'done' : null;
};

/**
 * Follow-up id a push/deep link points at: explicit id, else the follow-up linked to a calendar event.
 * @param {object} agenda normalized agenda. @param {object[]} done done items.
 * @param {{focus?: string, calendarEventId?: string}} [params] route params.
 * @returns {string} follow-up id or ''.
 */
export const resolveFocusId = (agenda, done, {focus, calendarEventId} = {}) => {
    if (focus) {
        return String(focus);
    }

    if (!calendarEventId || !agenda) {
        return '';
    }

    const all = [...asArray(agenda.overdue), ...asArray(agenda.today), ...asArray(agenda.upcoming && agenda.upcoming.followUps), ...asArray(done)];
    const hit = all.find((i) => String(idOf(i.calendarEvent) || '') === String(calendarEventId));

    return hit ? String(idOf(hit) || '') : '';
};

/**
 * Choices behind the "Megnyitás" button: the lead/customer record and, when linked, the calendar event.
 * @param {object} item follow-up.
 * @returns {Array<{key: 'entity'|'calendar', entityType?: string, id: string, date?: string}>} options (empty when nothing to open).
 */
export const buildOpenOptions = (item = {}) => {
    const e = entityOf(item);
    const options = [];

    if (e.id) {
        options.push({key: 'entity', entityType: e.type, id: e.id});
    }

    const eventId = String(idOf(item.calendarEvent) || '');

    if (eventId) {
        const ev = item.calendarEvent && typeof item.calendarEvent === 'object' ? item.calendarEvent : {};

        options.push({key: 'calendar', id: eventId, date: ev.start || item.dueAt || undefined});
    }

    return options;
};

/**
 * Lead-like object fed to the shared contact sheet (customers get a minimal one).
 * @param {object} item follow-up.
 * @returns {{_id: string, name: string, company: string, phone: string, email: string, status?: string, isCustomer: boolean}} subject.
 */
export const buildCompletionSubject = (item = {}) => {
    const e = entityOf(item);

    return {
        _id: e.id,
        name: e.name,
        company: e.company,
        phone: e.phone,
        email: e.email,
        ...(e.type === 'lead' ? {status: item.lead && item.lead.status} : {}),
        isCustomer: e.type === 'customer',
    };
};

/**
 * Safe agenda: every collection is an array, counts are numbers.
 * @param {object} raw GET /crm/follow-ups/agenda body.
 * @returns {object} normalized agenda.
 */
export const normalizeAgenda = (raw) => {
    const r = raw && typeof raw === 'object' ? raw : {};
    const c = r.counts || {};
    const num = (v) => Number(v) || 0;
    const up = r.upcoming || {};

    return {
        date: r.date || '',
        counts: {
            overdue: num(c.overdue), today: num(c.today), upcoming7d: num(c.upcoming7d),
            doneToday: num(c.doneToday), calendarToday: num(c.calendarToday), upcomingNext: num(c.upcomingNext),
            workToday: num(c.workToday), workOverdue: num(c.workOverdue),
        },
        // Old servers omit `access` -> follow-ups stay visible; only an explicit `false` hides them.
        access: {followUps: !(r.access && r.access.followUps === false)},
        workItems: normalizeWorkItems(r.workItems),
        overdue: asArray(r.overdue),
        today: asArray(r.today),
        next: r.next || null,
        calendarEvents: asArray(r.calendarEvents),
        byType: r.byType && typeof r.byType === 'object' ? r.byType : {},
        upcoming: {
            days: num(up.days) || 3,
            followUps: asArray(up.followUps),
            calendarEvents: asArray(up.calendarEvents),
            byDay: asArray(up.byDay).map((d) => ({
                date: d.date, followUps: asArray(d.followUps), calendarEvents: asArray(d.calendarEvents),
            })),
        },
    };
};

/**
 * Open items needing action now (overdue + today), used for badges and auto-open.
 * @param {object} agenda normalized agenda or `{counts}`.
 * @returns {number} count.
 */
export const actionableCount = (agenda) => {
    const c = (agenda && agenda.counts) || {};

    return (Number(c.overdue) || 0) + (Number(c.today) || 0);
};

/**
 * Progress "done / total" of the day.
 * @param {object} agenda normalized agenda.
 * @returns {{done: number, total: number, ratio: number}} progress.
 */
export const dayProgress = (agenda) => {
    const c = (agenda && agenda.counts) || {};
    const done = Number(c.doneToday) || 0;
    const total = done + (Number(c.today) || 0) + (Number(c.overdue) || 0);

    return {done, total, ratio: total > 0 ? done / total : 0};
};

/**
 * Readable text of any value an API field may carry: strings/numbers pass through, arrays are joined, and objects
 * (populated location / organizer / related entity / `{first, last}` names) resolve to their display field. Never
 * returns "[object Object]" — an object without a recognisable display field yields ''.
 * @param {*} value raw field value.
 * @param {number} [depth] recursion guard.
 * @returns {string} display text ('' when nothing readable).
 */
export const plainText = (value, depth = 0) => {
    if (value == null || depth > 3) {
        return '';
    }

    if (typeof value === 'string') {
        return value.trim();
    }

    if (typeof value === 'number' || typeof value === 'boolean') {
        return typeof value === 'boolean' ? '' : String(value);
    }

    if (Array.isArray(value)) {
        return value.map((v) => plainText(v, depth + 1)).filter(Boolean).join(', ');
    }

    if (typeof value === 'object') {
        if ('first' in value || 'last' in value) {
            return [plainText(value.first, depth + 1), plainText(value.last, depth + 1)].filter(Boolean).join(' ');
        }

        const keys = ['displayName', 'name', 'title', 'label', 'address', 'formattedAddress', 'formatted', 'fullName', 'company', 'text', 'email'];

        for (const key of keys) {
            const text = plainText(value[key], depth + 1);

            if (text) {
                return text;
            }
        }
    }

    return '';
};

/**
 * Secondary line parts of a calendar event: location, linked lead/customer, organizer — each as readable text,
 * empty/duplicate parts dropped. The time range is NOT included (cards show it in the time block).
 * @param {object} event agenda calendar event.
 * @returns {string[]} parts.
 */
export const eventSubtitleParts = (event = {}) => {
    const ev = event && typeof event === 'object' ? event : {};
    const parts = [ev.location, ev.lead, ev.customer, ev.entityName, ev.organizer].map((v) => plainText(v)).filter(Boolean);

    return parts.filter((p, i) => parts.indexOf(p) === i);
};

/**
 * Event subtitle joined with " · " ('' when there is nothing readable).
 * @param {object} event agenda calendar event.
 * @returns {string} subtitle.
 */
export const eventSubtitle = (event = {}) => eventSubtitleParts(event).join(' · ');

/**
 * Whole-day distance of a `YYYY-MM-DD` key from today (local zone).
 * @param {string} key day key. @param {Date} [now] reference.
 * @returns {number} 0 = today, 1 = tomorrow, negative = past; NaN for an invalid key.
 */
export const dayOffset = (key, now = new Date()) => {
    const [y, mo, d] = String(key).split('-').map(Number);
    const target = new Date(y, (mo || 1) - 1, d || 1);
    const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    return Math.round((target.getTime() - base.getTime()) / 86400000);
};

/**
 * Friendly label descriptor of an upcoming day: `today` / `tomorrow` (localised by the caller) or a formatted date.
 * @param {string} key day key. @param {Date} [now] reference.
 * @param {string} [locale] BCP-47 tag (invalid tags fall back to the device locale).
 * @returns {{kind: 'today'|'tomorrow'|'date', label: string}} descriptor; `label` is the formatted date ("Hétfő, okt. 12.").
 */
export const friendlyDay = (key, now = new Date(), locale) => {
    const off = dayOffset(key, now);
    const [y, mo, d] = String(key).split('-').map(Number);
    const date = new Date(y, (mo || 1) - 1, d || 1);
    const opts = {weekday: 'long', month: 'short', day: 'numeric'};
    let label;

    try {
        label = date.toLocaleDateString(locale || undefined, opts);
    } catch (_err) {
        label = date.toLocaleDateString(undefined, opts);
    }

    label = label.charAt(0).toUpperCase() + label.slice(1);

    return {kind: off === 0 ? 'today' : off === 1 ? 'tomorrow' : 'date', label};
};

/**
 * Upcoming days that actually hold something (empty days are hidden).
 * @param {object[]} byDay normalized `agenda.upcoming.byDay`.
 * @returns {object[]} non-empty days.
 */
export const nonEmptyDays = (byDay) => asArray(byDay).filter((d) => d.followUps.length + d.calendarEvents.length > 0);

/**
 * Inbox insight chips worth showing: only counters above zero.
 * @param {object} stats `insights.stats`.
 * @returns {Array<{key: string, value: number}>} non-zero chips in display order.
 */
export const inboxChips = (stats) => ['unread', 'unreadFromLeads', 'awaitingReply24h', 'buyingSignals']
    .map((key) => ({key, value: Number(stats && stats[key]) || 0}))
    .filter((c) => c.value > 0);

/**
 * Group items by LOCAL time of day (device zone): morning < 12, afternoon < 17, evening.
 * Items past their time keep their position; order inside a group is by dueAt.
 * @param {object[]} items follow-ups.
 * @returns {{morning: object[], afternoon: object[], evening: object[]}} groups.
 */
export const groupByTime = (items) => {
    const groups = {morning: [], afternoon: [], evening: []};

    [...asArray(items)].sort((a, b) => (toTime(a.dueAt) || 0) - (toTime(b.dueAt) || 0)).forEach((item) => {
        const t = toTime(item.dueAt);
        const h = t === null ? 0 : new Date(t).getHours();

        groups[h < 12 ? 'morning' : (h < 17 ? 'afternoon' : 'evening')].push(item);
    });

    return groups;
};

/**
 * Group items by follow-up type (unknown types fall into `other`); empty groups are kept out.
 * @param {object[]} items follow-ups.
 * @returns {{type: string, items: object[]}[]} groups in canonical type order.
 */
export const groupByType = (items) => {
    const map = {};

    asArray(items).forEach((item) => {
        const key = FOLLOW_UP_TYPE_KEYS.includes(item.type) ? item.type : 'other';

        (map[key] = map[key] || []).push(item);
    });

    return FOLLOW_UP_TYPE_KEYS.filter((k) => map[k]).map((type) => ({
        type,
        items: map[type].sort((a, b) => (toTime(a.dueAt) || 0) - (toTime(b.dueAt) || 0)),
    }));
};

/**
 * Countdown descriptor for a due date.
 * @param {string|Date} dueAt due date.
 * @param {Date} [now] reference.
 * @returns {{kind: 'overdue'|'now'|'future', minutes: number, hours: number, days: number}} descriptor;
 *   `minutes` is the absolute distance, `hours`/`days` are its floored larger units.
 */
export const countdownInfo = (dueAt, now = new Date()) => {
    const t = toTime(dueAt);

    if (t === null) {
        return {kind: 'future', minutes: 0, hours: 0, days: 0};
    }

    const diff = t - now.getTime();
    const minutes = Math.floor(Math.abs(diff) / 60000);
    const kind = diff < -60000 ? 'overdue' : (diff < 60000 ? 'now' : 'future');

    return {kind, minutes, hours: Math.floor(minutes / 60), days: Math.floor(minutes / 1440)};
};

/**
 * Translate a countdown into a localized string via a `t` function.
 * @param {string|Date} dueAt due date.
 * @param {Function} t i18n translate function.
 * @param {Date} [now] reference.
 * @returns {string} e.g. "12 perc múlva", "2 órája lejárt".
 */
export const countdownText = (dueAt, t, now = new Date()) => {
    const c = countdownInfo(dueAt, now);

    if (c.kind === 'now') {
        return t('sales.fu.cd_now', 'Most esedékes');
    }

    const unit = c.days >= 1 ? 'd' : (c.hours >= 1 ? 'h' : 'm');
    const count = c.days >= 1 ? c.days : (c.hours >= 1 ? c.hours : c.minutes);
    const prefix = c.kind === 'overdue' ? 'cd_ago' : 'cd_in';
    const defaults = {
        cd_in_m: '{{count}} perc múlva', cd_in_h: '{{count}} óra múlva', cd_in_d: '{{count}} nap múlva',
        cd_ago_m: '{{count}} perce lejárt', cd_ago_h: '{{count}} órája lejárt', cd_ago_d: '{{count}} napja lejárt',
    };

    return t(`sales.fu.${prefix}_${unit}`, defaults[`${prefix}_${unit}`], {count});
};

/**
 * The "Most következik" item: the server's `next`, else the earliest open today item not yet past,
 * else the earliest overdue one.
 * @param {object} agenda normalized agenda.
 * @param {Date} [now] reference.
 * @returns {object|null} follow-up.
 */
export const pickNext = (agenda, now = new Date()) => {
    if (!agenda) {
        return null;
    }

    const today = asArray(agenda.today).filter((i) => i.status !== 'done' && i.status !== 'cancelled');
    const upcomingToday = today.filter((i) => (toTime(i.dueAt) || 0) >= now.getTime() - 60000)
        .sort((a, b) => toTime(a.dueAt) - toTime(b.dueAt));

    if (upcomingToday[0]) {
        return upcomingToday[0];
    }

    if (agenda.next) {
        return agenda.next;
    }

    return asArray(agenda.overdue)[0] || today[0] || null;
};

/**
 * Normalize the contact-KPI response to the four dashboard numbers.
 * @param {object} stats GET /crm/statistics/contacts body.
 * @returns {{contacts: number, connectRate: number, avgToAdvance: number|null, overdueCallbacks: number}} KPIs.
 */
export const readContactKpis = (stats) => {
    const s = stats && typeof stats === 'object' ? (stats.data && !stats.activity ? stats.data : stats) : {};
    const avg = s.effectiveness && s.effectiveness.avgContactsToAdvance;
    const rate = Number(s.activity && s.activity.connectRate) || 0;

    return {
        contacts: Number(s.activity && s.activity.totalContacts) || 0,
        // The API may answer a 0..1 ratio or a 0..100 percentage.
        connectRate: Math.round(rate > 0 && rate <= 1 ? rate * 100 : rate),
        avgToAdvance: avg === null || avg === undefined || Number.isNaN(Number(avg)) ? null : Math.round(Number(avg) * 10) / 10,
        overdueCallbacks: Number(s.followUps && s.followUps.overdueCallbacks) || 0,
    };
};

/**
 * Build the body of `POST /crm/follow-ups`; drops empty optionals, validates locally.
 * @param {object} form {entityType, entity:{_id,source?}, type, dueAt, durationMinutes, note, requestedByClient}.
 * @param {Date} [now] reference.
 * @returns {{payload: object|null, error: string|null}} payload or error code
 *   (`entity` | `dueAt` | `past`).
 */
export const buildFollowUpCreatePayload = (form = {}, now = new Date()) => {
    const entityId = idOf(form.entity);

    if (!entityId) {
        return {payload: null, error: 'entity'};
    }

    const due = form.dueAt ? new Date(form.dueAt) : null;

    if (!due || Number.isNaN(due.getTime())) {
        return {payload: null, error: 'dueAt'};
    }

    if (due.getTime() <= now.getTime()) {
        return {payload: null, error: 'past'};
    }

    const entityType = form.entityType === 'customer' ? 'customer' : 'lead';
    const payload = {
        entityType,
        [entityType]: String(entityId),
        type: FOLLOW_UP_TYPE_KEYS.includes(form.type) ? form.type : 'call',
        dueAt: due.toISOString(),
    };

    if (entityType === 'customer' && form.entity) {
        const source = [form.entity.source, form.entity.sourceType].find((s) => CUSTOMER_SOURCES.includes(s));

        if (source) {
            payload.customerSource = source;
        }
    }

    const duration = Number(form.durationMinutes);

    if (Number.isFinite(duration) && duration > 0) {
        payload.durationMinutes = Math.round(duration);
    }

    const note = String(form.note || '').trim();

    if (note) {
        payload.note = note;
    }

    if (form.requestedByClient) {
        payload.requestedByClient = true;
    }

    return {payload, error: null};
};

/**
 * Body of `POST /{id}/complete` (one request: attempt + note + next follow-up).
 * @param {{contactAttempt?: object, completionNote?: string, nextFollowUp?: object}} [parts] parts.
 * @returns {object} body without empty keys.
 */
export const buildCompleteBody = (parts = {}) => {
    const body = {};
    const note = String(parts.completionNote || '').trim();

    if (parts.contactAttempt && typeof parts.contactAttempt === 'object') {
        const {applySuggestedStatus, ...attempt} = parts.contactAttempt;

        body.contactAttempt = applySuggestedStatus ? {...attempt, applySuggestedStatus: true} : attempt;
    }

    if (note) {
        body.completionNote = note;
    }

    if (parts.nextFollowUp && typeof parts.nextFollowUp === 'object') {
        body.nextFollowUp = parts.nextFollowUp;
    }

    return body;
};

/**
 * Should the to-do screen auto-open now? Once per user per local day, only with actionable items,
 * never when the user opted out for today.
 * @param {{count: number, lastOpenedDay: string|null, optOutDay: string|null, today: string}} state state.
 * @returns {boolean} true to open.
 */
export const shouldAutoOpen = ({count, lastOpenedDay, optOutDay, today}) => (
    Number(count) > 0 && lastOpenedDay !== today && optOutDay !== today
);

/**
 * Storage key of the auto-open marker.
 * @param {string} userId user id. @param {string} day local day key. @returns {string} key.
 */
export const autoOpenKey = (userId, day) => `${AUTO_OPEN_STORAGE_PREFIX}${userId}:${day}`;

/**
 * Storage key of the "don't show today" opt-out.
 * @param {string} userId user id. @param {string} day local day key. @returns {string} key.
 */
export const autoOpenOptOutKey = (userId, day) => `${AUTO_OPEN_OPTOUT_PREFIX}${userId}:${day}`;

export {resolveFollowUpTarget} from './followUpTarget';

/* ------------------------------ network ---------------------------------- */

const qs = (obj) => Object.entries(obj)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');

const json = (body) => JSON.stringify(body);

/**
 * GET agenda (overdue/today/groups/calendar/upcoming).
 * @param {{upcomingDays?: number}} [opts] options. @returns {Promise<object>} normalized agenda.
 */
export const fetchAgenda = async ({upcomingDays = 3} = {}) => normalizeAgenda(await apiFetch(
    `/crm/follow-ups/agenda?${qs({tzOffset: getTzOffset(), upcomingDays})}`, {skipCache: true},
));

/**
 * GET a bucket list (used for the "done" tab).
 * @param {string} bucket today|overdue|upcoming|done|all. @param {{page?: number, limit?: number}} [opts] paging.
 * @returns {Promise<{items: object[], total: number, counts: object}>} page.
 */
export const fetchFollowUps = async (bucket, {page = 1, limit = 50} = {}) => {
    const res = await apiFetch(`/crm/follow-ups?${qs({bucket, page, limit, tzOffset: getTzOffset()})}`, {skipCache: true});

    return {items: asArray(res && res.items), total: Number(res && res.total) || 0, counts: (res && res.counts) || {}};
};

/**
 * GET one follow-up (used when a pushed follow-up is in none of the loaded lists).
 * @param {string} id follow-up id. @returns {Promise<object|null>} follow-up or null.
 */
export const fetchFollowUp = async (id) => {
    const res = await apiFetch(`/crm/follow-ups/${id}`, {skipCache: true});
    const doc = res && (res.followUp || res);

    return doc && doc._id ? doc : null;
};

/** POST create. @param {object} payload from buildFollowUpCreatePayload. @returns {Promise<object>} follow-up. */
export const createFollowUp = (payload) => apiFetch('/crm/follow-ups', {method: 'POST', body: json(payload)});

/** PUT update (reschedule). @param {string} id id. @param {object} patch fields. @returns {Promise<object>} follow-up. */
export const updateFollowUp = (id, patch) => apiFetch(`/crm/follow-ups/${id}`, {method: 'PUT', body: json(patch)});

/**
 * POST complete — attempt, note and next follow-up in ONE request.
 * @param {string} id id. @param {object} parts see buildCompleteBody. @returns {Promise<object>} `{followUp, attempt, nextFollowUp}`.
 */
export const completeFollowUp = (id, parts) => apiFetch(`/crm/follow-ups/${id}/complete`, {
    method: 'POST', body: json(buildCompleteBody(parts)),
});

/**
 * POST snooze.
 * @param {string} id id. @param {{minutes?: number, until?: Date|string}} target target. @returns {Promise<object>} follow-up.
 */
export const snoozeFollowUp = (id, {minutes, until}) => apiFetch(`/crm/follow-ups/${id}/snooze`, {
    method: 'POST',
    body: json(until ? {until: new Date(until).toISOString()} : {minutes}),
});

/** POST cancel. @param {string} id id. @returns {Promise<object>} follow-up. */
export const cancelFollowUp = (id) => apiFetch(`/crm/follow-ups/${id}/cancel`, {method: 'POST', body: json({})});

/** GET unread-mail insights. @returns {Promise<{stats: object, items: object[], ai: any, aiAvailable: boolean}>} insights. */
export const fetchInboxInsights = async () => {
    const res = await apiFetch(`/crm/follow-ups/inbox-insights?${qs({tzOffset: getTzOffset()})}`, {skipCache: true});

    return {
        stats: (res && res.stats) || {},
        items: asArray(res && res.items),
        ai: (res && res.ai) || null,
        aiAvailable: res ? res.aiAvailable !== false : true,
    };
};

/**
 * POST AI analysis of the inbox (spends AI credits; 402/403 = none, error carries `status`).
 * @param {string} locale UI language. @returns {Promise<object>} insights with `ai`.
 */
export const analyzeInbox = (locale) => apiFetch('/crm/follow-ups/inbox-insights/analyze', {
    method: 'POST', body: json({tzOffset: getTzOffset(), locale}),
});

/**
 * GET weekly contact KPIs.
 * @param {string} [timeRange] range key. @returns {Promise<object>} normalized KPIs.
 */
export const fetchContactKpis = async (timeRange = 'week') => readContactKpis(
    await apiFetch(`/crm/statistics/contacts?${qs({timeRange, tzOffset: getTzOffset()})}`, {skipCache: true}),
);

/**
 * Cheap "how many need action" probe for badges / auto-open.
 * @returns {Promise<number>} overdue + today (0 on failure).
 */
export const fetchActionableCount = async () => {
    try {
        const res = await apiFetch(`/crm/follow-ups?${qs({bucket: 'today', limit: 1, tzOffset: getTzOffset()})}`, {skipCache: true});
        const c = (res && res.counts) || {};

        return (Number(c.overdue) || 0) + (Number(c.today) || 0);
    } catch (_e) {
        return 0;
    }
};

/**
 * Overdue / today counters for badges (`{overdue: 0, today: 0}` on failure).
 * @returns {Promise<{overdue: number, today: number}>} counters.
 */
export const fetchActionableCountParts = async () => {
    try {
        const res = await apiFetch(`/crm/follow-ups?${qs({bucket: 'today', limit: 1, tzOffset: getTzOffset()})}`, {skipCache: true});
        const c = (res && res.counts) || {};

        return {overdue: Number(c.overdue) || 0, today: Number(c.today) || 0};
    } catch (_e) {
        return {overdue: 0, today: 0};
    }
};
